'use client'

import { useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { CheckCircleIcon, FileDownIcon, ListIcon, PackageIcon, PlayIcon } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@bprogress/next/app'
import { toast } from 'sonner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingState, ProductsFilters } from '@/components/partials'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ProductsFilterParams } from '@/types/product'
import { CreateProductExcelExportParams } from '@/types/product-excel-export'
import { useProducts, useCreateProductExcelExport } from '@/hooks'
import { SortEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { mutate: createProductExcelExport, isPending: isCreating } = useCreateProductExcelExport()

  const filters = useMemo<ProductsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    const categoriesParam = params.get('categoryIds')
    const vehicleGroupsParam = params.get('vehicleGroupIds')
    const brandsParam = params.get('brandIds')
    const modelsParam = params.get('modelIds')
    const priceMinParam = params.get('priceMin')
    const priceMaxParam = params.get('priceMax')
    const stockMinParam = params.get('stockMin')
    const stockMaxParam = params.get('stockMax')

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      active: params.get('active') === 'true' ? 'true' : params.get('active') === 'false' ? 'false' : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined,
      categoryIds: categoriesParam ? categoriesParam.split(',').filter(Boolean) : undefined,
      vehicleGroupIds: vehicleGroupsParam ? vehicleGroupsParam.split(',').filter(Boolean) : undefined,
      brandIds: brandsParam ? brandsParam.split(',').filter(Boolean) : undefined,
      modelIds: modelsParam ? modelsParam.split(',').filter(Boolean) : undefined,
      priceMin: priceMinParam ? parseFloat(priceMinParam) : undefined,
      priceMax: priceMaxParam ? parseFloat(priceMaxParam) : undefined,
      stockMin: stockMinParam ? parseInt(stockMinParam) : undefined,
      stockMax: stockMaxParam ? parseInt(stockMaxParam) : undefined
    }
  }, [searchParams])

  const { data: productsData, isLoading, error, refetch } = useProducts(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<ProductsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (
          (key === 'createdUsers' ||
            key === 'updatedUsers' ||
            key === 'categoryIds' ||
            key === 'vehicleGroupIds' ||
            key === 'brandIds' ||
            key === 'modelIds') &&
          Array.isArray(value)
        ) {
          params.set(key, value.join(','))
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<ProductsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products/export/excel/create')
  }

  const handleStartExport = async () => {
    // Convert filters to CreateProductExcelExportParams by removing page and size
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, size, ...exportParams } = filters
    const createParams: CreateProductExcelExportParams = exportParams

    createProductExcelExport(createParams, {
      onSuccess: data => {
        setIsRedirecting(true)
        queryClient.invalidateQueries({ queryKey: ['productExcelExports'] })
        router.push(`/products/export/excel/${data.id}`)
      },
      onError: (error: any) => {
        toast.error(error?.message || t('error_starting_export'))
      }
    })
  }

  if (isLoading) {
    return <LoadingState isLoading={true}>{t('loading')}</LoadingState>
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('error')}</CardTitle>
          <CardDescription>{t('error_loading_record')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()}>{t('try_again')}</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileDownIcon className="h-5 w-5" />
                {t('export_as_excel')}
              </CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => router.push('/products/export/excel')}>
                <ListIcon />
                <span className="hidden items-center gap-2 lg:flex">{t('list')}</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const currentParams = searchParams.toString()
                  const targetUrl = `/products${currentParams ? `?${currentParams}` : ''}`
                  router.push(targetUrl)
                }}>
                <PackageIcon />
                <span className="hidden items-center gap-2 lg:flex">{t('products')}</span>
              </Button>
              <ProductsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        {productsData && (
          <CardContent>
            <Alert>
              <CheckCircleIcon />
              <AlertTitle>
                {t('number_of_items_found')}: {productsData.total}
              </AlertTitle>
              {productsData.total > 0 && (
                <AlertDescription>
                  <Button className="mt-2" onClick={handleStartExport} disabled={isCreating || isRedirecting}>
                    <PlayIcon />
                    {isCreating ? t('starting') : t('start')}
                  </Button>
                </AlertDescription>
              )}
            </Alert>
          </CardContent>
        )}
      </Card>
    </>
  )
}

export default Create
